import { useEffect } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';

/**
 * Keep a group of Mapbox layers above everything else, in the given order.
 * Layers mounted later (toggling points / dwells / cameras …) are appended on
 * top by Mapbox, so the order is re-asserted whenever the style changes.
 * `getMap` must be referentially stable (useCallback / ref-based).
 */
export function useKeepLayersOnTop(
  getMap: () => MapboxMap | undefined | null,
  layerIds: readonly string[],
  enabled: boolean,
): void {
  const key = layerIds.join('|');
  useEffect(() => {
    const m = getMap();
    if (!m || !enabled || layerIds.length === 0) return;
    let raf = 0;
    const toTop = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        try {
          if (layerIds.some((id) => !m.getLayer(id))) return;
          const layers = m.getStyle()?.layers ?? [];
          const n = layers.length;
          // Already [.., a, b, c] in order → nothing to do (also stops the
          // styledata → moveLayer → styledata loop).
          const tail = layers.slice(Math.max(0, n - layerIds.length)).map((l) => l.id);
          if (tail.length === layerIds.length && tail.every((id, i) => id === layerIds[i])) return;
          for (const id of layerIds) m.moveLayer(id);
        } catch {
          /* style mid-transition */
        }
      });
    };
    toTop();
    m.on('styledata', toTop);
    return () => {
      cancelAnimationFrame(raf);
      m.off('styledata', toTop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMap, key, enabled]);
}

export default useKeepLayersOnTop;
